import React, { useRef, useState } from "react";
import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import {
  ChevronLeft,
  File,
  Database,
  Maximize,
  User,
  Copy,
  SquareCheckBig,
  Pencil,
  Info,
} from "lucide-react";
import { VerificationResponse } from "../types";
import VerificationFailure from "./VerificationFailure";

// Import your custom SVGs
import blockchainSvg from "../assets/images/svg/blockchain.svg";
import cameraSvg from "../assets/images/svg/camera.svg";
import codeiconSvg from "../assets/images/svg/codeicon.svg";
import fileSvg from "../assets/images/svg/file.svg";
import secureSvg from "../assets/images/svg/secure.svg";
import verifiedtickSvg from "../assets/images/svg/verifiedtick.svg";
import verifiedticksSvg from "../assets/images/svg/verifiedticks.svg";
import frameSvg from "../assets/images/svg/frame.svg";
import loopboxSvg from "../assets/images/svg/loopbox.svg";
import fluentPageSvg from "../assets/images/svg/fluent-mdl2_page.svg";
import healthiconsYesOutlineSvg from "../assets/images/svg/healthicons_yes-outline.svg";
interface VerificationResultProps {
  result: VerificationResponse;
  onNewVerification: () => void;
}

/* ============================================================
   Reusable presentation pieces (styled per Figma / vr-* classes)
   ============================================================ */

type DetailRowProps = {
  label: string;
  value: ReactNode;
  hasCopy?: boolean;
  copyText?: string;
  /** Let the value wrap onto multiple lines instead of truncating with "…". */
  wrap?: boolean;
};

function DetailRow({
  label,
  value,
  hasCopy = false,
  copyText,
  wrap = false,
}: DetailRowProps) {
  const handleCopy = async () => {
    if (!copyText) return;

    try {
      await navigator.clipboard.writeText(copyText);
    } catch (error) {
      console.error("Unable to copy value:", error);
    }
  };

  return (
    <div
      className="
        flex min-w-0 items-center justify-between
        gap-2.5 border-b border-[#787777]
        px-2.5 py-[7px]
        last:border-b-0
        sm:gap-4 sm:px-3 sm:py-[10px]
      "
    >
      <div className="vr-detail-label shrink-0 whitespace-nowrap text-[12px] leading-none sm:text-[16px]">
        {label}
      </div>

      <div className={`vr-detail-value flex min-w-0 flex-1 justify-end gap-1 text-right text-[12px] sm:gap-2 sm:text-[16px] ${wrap ? "items-start leading-snug" : "items-center leading-none"}`}>
        <span className={wrap ? "min-w-0 whitespace-normal break-words" : "min-w-0 truncate"}>{value}</span>

        {hasCopy && (
          <button
            type="button"
            onClick={handleCopy}
            className="
              flex shrink-0 items-center justify-center
              bg-transparent p-0 text-[#5F5F5F]
              transition-opacity hover:opacity-70
            "
            aria-label={`Copy ${label}`}
          >
            <Copy
              aria-hidden="true"
              strokeWidth={1.8}
              className="h-[13px] w-[13px] sm:h-[17px] sm:w-[17px]"
            />
          </button>
        )}
      </div>
    </div>
  );
}

type CardProps = {
  title: string;
  icon: LucideIcon | string;
  children: ReactNode;
  className?: string;
};

function Card({
  title,
  icon: Icon,
  children,
  className = "",
}: CardProps) {
  return (
    <article
      className={`mb-5 overflow-hidden rounded-[8px] border border-[#A8A8A8] bg-white ${className}`}
    >
      <div className="mx-2 flex items-center gap-3 border-b border-[#787777] px-2 py-[13px] sm:mx-3 sm:px-3 sm:py-4">
        {typeof Icon === "string" ? (
          <img
            src={Icon}
            alt=""
            className="h-[17px] w-[17px] shrink-0 sm:h-[22px] sm:w-[22px]"
          />
        ) : (
          <Icon
            strokeWidth={2}
            className="h-[17px] w-[17px] shrink-0 text-[#00783F] sm:h-[22px] sm:w-[22px]"
          />
        )}

        <h2 className="vr-heading-black text-[15px] sm:text-[18px]">
          {title}
        </h2>
      </div>

      <div className="px-2 sm:px-3">{children}</div>
    </article>
  );
}

/* ============================================================
   MAIN COMPONENT
   ============================================================ */

const VerificationResult: React.FC<VerificationResultProps> = ({
  result,
  onNewVerification,
}) => {
  // Duration derived from the loaded media element (video/audio), used when the
  // backend metadata doesn't carry a duration.
  const [mediaDuration, setMediaDuration] = useState<number | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const isVerified = result.success && result.data?.verified;
  const data = result.data;
  const integrity = data?.integrity;
  // Render the web-displayable preview when the certified original is a format
  // browsers can't show (HEIC image / HEVC video); fall back to the original.
  const displayUrl = data?.media?.displayUrl || data?.media?.mediaUrl;

  const formatDate = (d?: string) => {
    if (!d) return "Unknown";

    try {
      return new Date(d).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return d;
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes || bytes <= 0) return "Unknown";

    const mb = bytes / (1024 * 1024);

    return mb < 1
      ? `${(bytes / 1024).toFixed(2)} KB`
      : `${mb.toFixed(2)} MB`;
  };

  // Duration as [H:]MM:SS, with millisecond precision when withMillis (videos).
  const formatDuration = (seconds?: number | null, withMillis = false) => {
    if (seconds == null || !isFinite(seconds) || seconds <= 0) return null;

    const totalMs = Math.round(seconds * 1000);
    const ms = totalMs % 1000;
    const totalSec = Math.floor(totalMs / 1000);
    const s = totalSec % 60;
    const min = Math.floor(totalSec / 60) % 60;
    const h = Math.floor(totalSec / 3600);
    const two = (n: number) => String(n).padStart(2, "0");

    const base = h > 0 ? `${h}:${two(min)}:${two(s)}` : `${min}:${two(s)}`;

    // Milliseconds are separated with a colon (MM:SS:mmm), not a dot.
    return withMillis ? `${base}:${String(ms).padStart(3, "0")}` : base;
  };

  const isVideoMedia = () => {
    const mime = data?.media?.mimeType;
    const url = data?.media?.mediaUrl || "";

    if (mime) return mime.startsWith("video/");

    return /\.(mp4|webm|mov|m4v)$/i.test(url);
  };

  const isAudioMedia = () => {
    if (data?.media?.isAudio) return true;

    const mime = data?.media?.mimeType;

    if (mime) return mime.startsWith("audio/");

    const url = data?.media?.mediaUrl || "";

    return /\.(mp3|wav|ogg|aac|m4a|flac|wma|opus|aiff?)$/i.test(url);
  };

  /* --------- error state --------- */

  // Both a failed request and a "no matching record" result are surfaced with
  // the same detailed Vastavik ID / NFT search-failed screen.
  if (!result.success || !isVerified) {
    return (
      <VerificationFailure
        variant="id"
        onReset={onNewVerification}
      />
    );
  }

  /* --------- happy path data --------- */

  const ownerName = data?.owner?.ownerName || "Anonymous";

  const initials =
    ownerName
      .split(" ")
      .map((s) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "VK";

  const m: any = data?.media || {};

  const filename =
    m.filename ||
    m.originalFilename ||
    "media";

  const fileSize =
    m.fileSize ??
    m.size ??
    m.originalFileSize;

  const mimeType = m.mimeType;

  // Duration (seconds) — prefer backend metadata, fall back to the loaded
  // media element (video/audio). Videos show millisecond precision.
  const durationSeconds =
    m.duration ??
    m.durationSeconds ??
    m.metadata?.duration ??
    m.audioMetadata?.duration ??
    mediaDuration;

  const dimensions =
    m.dimensions &&
    m.dimensions.width &&
    m.dimensions.height
      ? `${m.dimensions.width} × ${m.dimensions.height}`
      : typeof m.dimensions === "string"
        ? m.dimensions
        : null;

  const audio = isAudioMedia();
  const video = isVideoMedia();

  const mediaTypeLabel = audio
    ? "Audio"
    : video
      ? "Video"
      : "Image";

  const nftTokenId =
    data?.blockchain?.nftTokenId ||
    (data as any)?.nftTokenId;

  const lastVerified =
    data?.verification?.lastVerified ||
    new Date().toISOString();

  const verificationCount =
    data?.verification?.verificationCount ?? 0;

  /* --- shared sub-cards --- */

  // Order: File name, File Size, Duration (video/audio), MIME type, Upload
  // date & time. Duration shows millisecond precision for video.
  const durationLabel = video ? "Video Duration" : "Audio Duration";
  const durationValue = formatDuration(durationSeconds, video);

  const MediaInformation = (
    <Card title="Media Information" icon={fileSvg}>
      <DetailRow
        label="File name"
        value={filename}
        hasCopy
        copyText={filename}
      />

      <DetailRow
        label="File Size"
        value={formatFileSize(fileSize)}
      />

      {(video || audio) && (
        <DetailRow
          label={durationLabel}
          value={durationValue || "Unknown"}
        />
      )}

      <DetailRow
        label="MIME type"
        value={mimeType || "Unknown"}
      />

      <DetailRow
        label="Upload Date & Time"
        value={formatDate(
          m.uploadedAt ||
          data?.owner?.registrationDate
        )}
      />

      {dimensions && (
        <DetailRow
          label="Dimensions"
          value={`${dimensions} px`}
        />
      )}
    </Card>
  );

  const BlockchainInformation = (
    <Card
      title="Blockchain Information"
      icon={blockchainSvg}
    >
      <DetailRow
        label="Blockchain Registered"
        value={
          <span className="inline-flex items-center gap-1">
            <SquareCheckBig
              aria-hidden="true"
              strokeWidth={1.8}
              className="h-[14px] w-[14px] shrink-0 text-[#5F5F5F] sm:h-[18px] sm:w-[18px]"
            />

            <span>Yes</span>
          </span>
        }
      />

      {nftTokenId && (
        <DetailRow
          label="NFT Token ID"
          value={nftTokenId}
          hasCopy
          copyText={nftTokenId}
        />
      )}

      <DetailRow
        label="Registration Date"
        value={formatDate(
          data?.blockchain?.registrationDate ||
          data?.owner?.registrationDate
        )}
      />
    </Card>
  );

  const TechnicalDetails = (
    <Card
      title="Technical Details"
      icon={codeiconSvg}
    >
      <DetailRow
        label="Verification method"
        value="VASTAVIK ID"
      />

      <DetailRow
        label="Last Verified"
        value={formatDate(lastVerified)}
      />

      <DetailRow
        label="Media Type"
        value={mediaTypeLabel}
      />

      <DetailRow
        label="C2PA compliant"
        value={integrity?.c2paCompliant ? "Yes" : "No"}
      />
    </Card>
  );

  const AuthenticationIntegrity = (
    <Card
      title="Authentication & Integrity"
      icon={secureSvg}
    >
      <div className="flex items-center justify-between gap-2 border-b border-[#787777] px-2.5 py-[11px] sm:px-3 sm:py-[13px]">
        <div className="vr-subheading-black flex min-w-0 items-center gap-2 whitespace-nowrap text-[12px] sm:gap-2.5 sm:text-[16px]">
          <img
            src={verifiedtickSvg}
            alt="Authentic"
            className="h-[15px] w-[15px] shrink-0 sm:h-[18px] sm:w-[18px]"
          />

          Authenticity Check
        </div>

        <div className="inline-flex shrink-0 items-center justify-center gap-1 rounded-full border border-[#66A888] bg-white px-3 py-[2px] sm:gap-1.5 sm:py-1">
          <img
            src={verifiedticksSvg}
            alt=""
            className="h-[12px] w-[12px] sm:h-[14px] sm:w-[14px]"
          />

          <span className="vr-badge-text text-[11px] leading-none sm:text-[14px]">
            Original
          </span>
        </div>
      </div>

      <div className="border-b border-[#787777] px-2.5 py-4 sm:px-3 sm:py-4">
        <div className="vr-subheading-black mb-2.5 flex items-center gap-2 text-[12px] sm:gap-2.5 sm:text-[16px]">
          <img
            src={frameSvg}
            alt="Scan"
            className="h-[15px] w-[15px] sm:h-[18px] sm:w-[18px]"
          />

          Deepfake Detection
        </div>

        <p className="vr-description pl-[23px] text-left text-[11px] leading-[1.35] sm:pl-[28px] sm:text-[15px] sm:leading-[1.35]">
          No signs of AI manipulation or deepfake detected in this media.
        </p>
      </div>

      <DetailRow
        label="Is this media edited?"
        value={
          <span className="inline-flex items-center gap-1.5">
            {integrity?.edited ? (
              <Pencil
                aria-hidden="true"
                strokeWidth={1.8}
                className="h-[13px] w-[13px] shrink-0 text-[#5F5F5F] sm:h-[17px] sm:w-[17px]"
              />
            ) : (
              <SquareCheckBig
                aria-hidden="true"
                strokeWidth={1.8}
                className="h-[13px] w-[13px] shrink-0 text-[#00783F] sm:h-[17px] sm:w-[17px]"
              />
            )}
            <span>{integrity?.edited ? "Yes" : "No"}</span>
          </span>
        }
      />

      {integrity?.editorSoftware ? (
        <DetailRow label="Editing tool" value={integrity.editorSoftware} />
      ) : null}

      {integrity?.verifiedAgainstOriginal && integrity?.percentChanged != null ? (
        <DetailRow
          label="Match with original"
          wrap
          // Show the MATCH as a percentage out of 100 (how much of the media
          // matches the verified original), with the changed amount in brackets —
          // "8.1% changed" alone reads like a low score.
          value={`${Math.max(0, Math.min(100, 100 - Number(integrity.percentChanged))).toFixed(1)}% match (${Number(integrity.percentChanged).toFixed(1)}% enhanced/edited)`}
        />
      ) : null}
    </Card>
  );

  /* --- Media Preview --- */

  const MediaPreview =
    data?.media?.mediaUrl || m.voiceoverUrl ? (
      <Card
        title={
          audio
            ? "Audio Preview"
            : video
              ? "Video Preview"
              : "Media Preview"
        }
        icon={cameraSvg}
      >
        <div className="px-1 py-3 sm:px-0 sm:py-4">
          {audio ? (
            <div className="space-y-4">
              {data?.media?.mediaUrl && (
                <div>
                  <div className="vr-preview-value mb-1.5 text-[12px] sm:text-[16px]">
                    Original Audio
                  </div>

                  <audio
                    src={data.media.mediaUrl}
                    controls
                    className="w-full"
                    onLoadedMetadata={(e) =>
                      setMediaDuration(
                        (e.target as HTMLAudioElement).duration
                      )
                    }
                  />
                </div>
              )}

              {m.voiceoverUrl && (
                <div>
                  <div className="vr-preview-value mb-1.5 text-[12px] sm:text-[16px]">
                    Vastavik Voice ID
                    {m.voiceoverGender
                      ? ` (${m.voiceoverGender})`
                      : ""}
                  </div>

                  <audio
                    src={m.voiceoverUrl}
                    controls
                    className="w-full"
                  />
                </div>
              )}

              {!data?.media?.mediaUrl && !m.voiceoverUrl && (
                <div className="vr-preview-value text-[12px] sm:text-[16px]">
                  Audio preview unavailable.
                </div>
              )}
            </div>
          ) : video ? (
            <div className="mb-4 flex justify-center overflow-hidden rounded-[8px] border border-[#A8A8A8] bg-black sm:mb-5">
              <video
                ref={videoRef}
                src={displayUrl}
                controls
                className="max-h-[420px] w-auto"
                onLoadedMetadata={(e) =>
                  setMediaDuration(
                    (e.target as HTMLVideoElement).duration
                  )
                }
              />
            </div>
          ) : (
<img
  src={displayUrl}
  alt={filename}
  className="mb-4 aspect-[276/248] w-full rounded-[8px] border border-[#A8A8A8] bg-black object-contain sm:mb-5 sm:h-[310px] sm:aspect-auto"
/>
          )}

          <div className="flex flex-col gap-[13px] px-1 sm:gap-4 sm:px-2">
            {mimeType && (
              <div className="vr-preview-value flex items-center gap-3 text-[12px] leading-none sm:text-[16px]">
                <img
                  src={fluentPageSvg}
                  alt="File Type"
                  className="h-[15px] w-[15px] sm:h-[20px] sm:w-[20px]"
                />

                {mimeType}
              </div>
            )}

            {fileSize > 0 && (
              <div className="vr-preview-value flex items-center gap-3 text-[12px] leading-none sm:text-[16px]">
                <img
                  src={loopboxSvg}
                  alt="File Size"
                  className="h-[15px] w-[15px] sm:h-[20px] sm:w-[20px]"
                />

                {formatFileSize(fileSize)}
              </div>
            )}

            {dimensions && (
              <div className="vr-preview-value flex items-center gap-3 text-[12px] leading-none sm:text-[16px]">
                <img
                  src={frameSvg}
                  alt="Dimensions"
                  className="h-[15px] w-[15px] sm:h-[20px] sm:w-[20px]"
                />

                {dimensions}
              </div>
            )}
          </div>
        </div>
      </Card>
    ) : null;

  return (
    <main className="vr-page min-h-screen bg-white px-4 pb-12 pt-9 sm:px-6 sm:pt-10">
      <div className="mx-auto w-full max-w-[306px] sm:max-w-[612px] lg:max-w-[1024px]">
        {/* Top navigation */}

        <header className="mb-4 flex w-full items-center justify-between sm:mb-5">
          <button
            type="button"
            onClick={onNewVerification}
            className="inline-flex items-center gap-1.5 font-productsans-regular text-[12px] leading-none text-[#000000] transition-opacity hover:opacity-70 sm:gap-2 sm:text-[16px]"
          >
            <ChevronLeft
              strokeWidth={2}
              className="h-[15px] w-[15px] text-[#00783F] sm:h-5 sm:w-5"
            />

            <span>Back to Verification</span>
          </button>

          <div className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[#66A888] bg-white px-3 py-[3px] sm:gap-2 sm:px-4 sm:py-1.5">
            <img
              src={verifiedticksSvg}
              alt="Verified"
              className="h-[13px] w-[13px] sm:h-[18px] sm:w-[18px]"
            />

            <span className="font-productsans-regular text-[11px] leading-none text-[#4D4D4D] sm:text-[16px]">
              Verified
            </span>
          </div>
        </header>

        <div className="grid grid-cols-1 gap-x-8 gap-y-0 lg:grid-cols-[612px_minmax(0,1fr)]">
          {/* Desktop left column */}

          <div className="contents lg:flex lg:flex-col">
            <article className="order-1 mb-5 flex min-h-[98px] items-center gap-[25px] rounded-[8px] border border-[#A8A8A8] bg-white px-[17px] py-[14px] sm:min-h-[126px] sm:gap-6 sm:px-7 sm:py-5 lg:order-none">
              <div className="flex shrink-0 items-center justify-center">
               <img
  src={healthiconsYesOutlineSvg}
  alt="Verified Status"
  className="h-[60px] w-[60px] sm:h-[76px] sm:w-[76px]"
/>
              </div>

              <div className="min-w-0 text-left">
             

                <p className="vr-summary-text text-[12px] leading-[1.2] sm:text-[16px] sm:leading-[1.1]">
                  This media has been verified as{" "}
                  <span className="text-[#00783F]">
                    authentic and original
                  </span>
                </p>

                {video && (
                  <div className="mt-3 flex items-start gap-2 rounded-[6px] border border-[#E2E2E2] bg-[#F7F7F8] px-3 py-2">
                    <Info
                      aria-hidden="true"
                      strokeWidth={1.8}
                      className="mt-[1px] h-[13px] w-[13px] shrink-0 text-[#5F5F5F] sm:h-[16px] sm:w-[16px]"
                    />
                    <p className="text-left text-[11px] leading-[1.35] text-[#4D4D4D] sm:text-[13px]">
                      Please watch the video in full before confirming its
                      authenticity. Validation applies to the complete original
                      recording — review the entire video to ensure it has not
                      been trimmed, clipped, or altered.
                    </p>
                  </div>
                )}
              </div>
            </article>

            <div className="order-4 lg:order-none">
              {MediaInformation}
            </div>

            <div className="order-5 lg:order-none">
              {BlockchainInformation}
            </div>

            <div className="order-6 lg:order-none">
              {TechnicalDetails}
            </div>
          </div>

          {/* Desktop right column */}

          <div className="contents lg:flex lg:flex-col">
            <div className="order-2 lg:order-none">
              {MediaPreview}
            </div>

            <div className="order-3 lg:order-none">
              {AuthenticationIntegrity}
            </div>
          </div>
        </div>

        {/* Creator Information */}

        <article className="mb-8 overflow-hidden rounded-[8px] border border-[#A8A8A8] bg-white">
          <div className="flex flex-col md:flex-row">
            <div className="flex flex-col items-center justify-center px-5 pb-6 pt-4 md:w-[32%] md:border-r md:border-[#787777] md:px-7 md:py-8">
              <div className="flex h-[68px] w-[68px] items-center justify-center rounded-full border border-[#5F5F5F] bg-[#E3F2E7] sm:h-[80px] sm:w-[80px]">
                <span className="font-productsans-regular text-[30px] leading-none text-[#00783F] sm:text-[34px]">
                  {initials}
                </span>
              </div>

              <h2 className="vr-subheading-black mt-4 text-center text-[15px] sm:text-[18px]">
                {ownerName}
              </h2>
            </div>

            <div className="min-w-0 flex-1 px-2 pb-0 sm:px-3 md:py-4">
              <div className="flex items-center gap-3 border-b border-[#787777] px-2 py-[13px] sm:px-3 sm:py-4">
                <User
                  strokeWidth={2}
                  className="h-[17px] w-[17px] text-[#00783F] sm:h-[22px] sm:w-[22px]"
                />

                <h2 className="vr-heading-black text-[15px] sm:text-[18px]">
                  Creator Information
                </h2>
              </div>

              <DetailRow
                label="Creator ID"
                value={
                  data?.owner?.ownerId ||
                  data?.owner?.ownerKey ||
                  "—"
                }
                hasCopy
                copyText={
                  data?.owner?.ownerId ||
                  data?.owner?.ownerKey ||
                  ""
                }
              />

              <DetailRow
                label="VASTAVIK ID"
                value={data?.owner?.ownerKey || "—"}
              />

              <DetailRow
                label="Verification Account"
                value={`${verificationCount} Time(s)`}
              />
            </div>
          </div>
        </article>

        {/* Verify button */}

        <div className="flex justify-center pb-2 sm:pb-12">
          <button
            type="button"
            onClick={onNewVerification}
            className="flex h-[46px] w-[249px] items-center justify-center rounded-[8px] bg-[#087B45] px-6 font-caviar-bold text-[16px] text-white transition-opacity hover:opacity-90 sm:h-auto sm:w-auto sm:min-w-[300px] sm:px-10 sm:py-4 sm:text-[18px]"
          >
            Verify Another Media
          </button>
        </div>
      </div>
    </main>
  );
};

export default VerificationResult;